import { apiRequest } from '../lib/api-client'

/**
 * Asset types for agency assets
 */
export type AssetType = 'headerLogo' | 'footerLogo'

/**
 * Response from uploading an asset
 */
export interface UploadAssetResponse {
  success: boolean
  assetType: AssetType
  key: string
  contentType: string
}

/**
 * Response from checking if an asset exists
 */
export interface CheckAssetResponse {
  exists: boolean
  assetType: AssetType
  key?: string
  contentType?: string
}

/**
 * Response from deleting an asset
 */
export interface DeleteAssetResponse {
  success: boolean
  message: string
}

/**
 * Response for signed asset URL requests
 */
export interface SignedAssetUrlResponse {
  url: string
  expiresIn: number
  assetType: AssetType
  contentType?: string
}

/**
 * Upload an agency asset (headerLogo or footerLogo)
 * Images will be automatically converted to PNG (512px square, transparent background)
 * if they are raster images. SVG images are stored as-is.
 */
async function uploadAgencyAsset(
  agencyId: string,
  assetType: AssetType,
  file: File
): Promise<UploadAssetResponse> {
  return apiRequest<UploadAssetResponse>(
    `/agencies/${agencyId}/assets/${assetType}`,
    {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    }
  )
}

/**
 * Get an agency asset URL
 * Returns the full URL to the asset that can be used in img src
 */
function getAgencyAssetUrl(agencyId: string, assetType: AssetType): string {
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
  return `${apiBaseUrl}/agencies/${agencyId}/assets/${assetType}`
}

/**
 * Public, CORS-safe proxy URL for agency assets (no auth required)
 */
function getPublicAgencyAssetUrl(
  agencyId: string,
  assetType: AssetType
): string {
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
  return `${apiBaseUrl}/public/agency-assets/${agencyId}/${assetType}`
}

/**
 * Request a signed URL for an agency asset so it can be used without credentials
 */
async function getSignedAgencyAssetUrl(
  agencyId: string,
  assetType: AssetType
): Promise<SignedAssetUrlResponse> {
  return apiRequest<SignedAssetUrlResponse>(
    `/agencies/${agencyId}/assets/${assetType}/url`,
    {
      method: 'GET',
    }
  )
}

async function getAgencyAsset(
  agencyId: string,
  assetType: AssetType
): Promise<Blob> {
  const url = getAgencyAssetUrl(agencyId, assetType)

  const response = await fetch(url, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch asset: ${response.status} ${response.statusText}`
    )
  }

  return await response.blob()
}

/**
 * Delete an agency asset
 */
async function deleteAgencyAsset(
  agencyId: string,
  assetType: AssetType
): Promise<DeleteAssetResponse> {
  return apiRequest<DeleteAssetResponse>(
    `/agencies/${agencyId}/assets/${assetType}`,
    {
      method: 'DELETE',
    }
  )
}

/**
 * Check if an agency asset exists
 */
async function checkAgencyAsset(
  agencyId: string,
  assetType: AssetType
): Promise<CheckAssetResponse> {
  return apiRequest<CheckAssetResponse>(
    `/agencies/${agencyId}/assets/${assetType}/check`,
    {
      method: 'GET',
    }
  )
}

/**
 * Response from uploading a design-editor image (ImageBlock.assetKey source).
 * Unlike headerLogo/footerLogo, there's no singleton slot to track — the
 * returned `assetKey` is stored directly on the ImageBlock within the
 * design JSON, saved separately via AgencyService.updateAgencyPreferences.
 */
export interface UploadDesignImageResponse {
  success: boolean
  assetKey: string
  hash: string
  contentType: string
}

/**
 * Upload an image used by the WYSIWYG design editor. Returns an `assetKey`
 * to store on the ImageBlock — never persist inline base64 to the server.
 * `variant: 'crop'` relaxes the logo-tuned downscale for page-region crops
 * (setup wizard logo import).
 */
async function uploadDesignImageAsset(
  agencyId: string,
  file: File,
  opts: { variant?: 'crop' } = {}
): Promise<UploadDesignImageResponse> {
  const query = opts.variant ? `?variant=${opts.variant}` : ''
  return apiRequest<UploadDesignImageResponse>(
    `/agencies/${agencyId}/design-assets${query}`,
    {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    }
  )
}

/**
 * Public URL for a design-editor image, given the assetKey returned by
 * uploadDesignImageAsset. `assetKey` is a full S3 key
 * (agency-assets/{agencyId}/designImage-{hash}{ext}); only the filename
 * portion is sent, since the route reconstructs the full key server-side.
 */
function getDesignImageAssetUrl(agencyId: string, assetKey: string): string {
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000'
  const filename = assetKey.split('/').pop() ?? assetKey
  return `${apiBaseUrl}/agencies/${agencyId}/design-assets/${filename}`
}

/**
 * Agency asset service
 */
export const AgencyAssetService = {
  uploadAgencyAsset,
  getAgencyAssetUrl,
  getPublicAgencyAssetUrl,
  getSignedAgencyAssetUrl,
  getAgencyAsset,
  deleteAgencyAsset,
  checkAgencyAsset,
  uploadDesignImageAsset,
  getDesignImageAssetUrl,
}
